import { Component, OnInit, ViewChild } from '@angular/core';
import { ReportsService, RequestedReport } from '../reports.service';
import { JsonResponse } from '../../shared/api/backend-config';
import { activeReportTypesToTitle } from '../order-report/choose-type/choose-type.component';
import { takeWhile, timer } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { ReportStatusToTooltipPipe } from './report-status-to-tooltip.pipe';
import { StatusToTextPipe } from './status-to-text.pipe';
import { UtcToLocalPipe } from '../../shared/pipes/utc-to-local.pipe';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { MatDivider } from '@angular/material/divider';
import {
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { MatTooltip } from '@angular/material/tooltip';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { IconComponent } from '../../shared/components/icon/icon.component';

export enum ReportStatus {
  // report was ordered but not paid with stripe
  // insufficient card is here as well
  ORDERED_NOT_PAID = 'ORDERED_NOT_PAID',
  // payment was made but there were tricky errors
  PAYMENT_ERROR = 'PAYMENT_ERROR',
  // report ordered, payment was successful
  ORDERED = 'ORDERED',
  // backend processes the report
  IN_PROGRESS_AUTOMATED = 'IN_PROGRESS_AUTOMATED',
  // backend got error processing the report
  AUTOMATION_ERROR = 'AUTOMATION_ERROR',
  // backend processed the report, manual review is needed
  IN_PROGRESS_MANUAL = 'IN_PROGRESS_MANUAL',
  // report is ready to be downloaded
  READY = 'READY',
}

@Component({
  selector: 'app-requested-reports',
  templateUrl: './requested-reports.component.html',
  styleUrls: ['./requested-reports.component.scss', '../../app.component.scss'],
  imports: [
    NgIf,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    NgClass,
    MatTooltip,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NgFor,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatDivider,
    IconComponent,
    RouterLink,
    RouterLinkActive,
    DatePipe,
    UtcToLocalPipe,
    StatusToTextPipe,
    ReportStatusToTooltipPipe,
  ],
})
export class RequestedReportsComponent implements OnInit {
  @ViewChild('table', { static: false }) table!: MatTable<RequestedReport>;

  public requestedReports: RequestedReport[] = [];
  public isSomeReportInProgress = false;

  constructor(private reportsService: ReportsService) {}

  ngOnInit(): void {
    this.loadOrderedReports();

    this.reportsService.pollingSubj.subscribe(() => {
      this.loadOrderedReports();
    });
  }

  private loadOrderedReports(): void {
    this.requestedReports.length = 0;

    timer(0, 2000)
      .pipe(
        switchMap(() => this.reportsService.getRequestedReports()),
        tap((data: JsonResponse<RequestedReport[]>) => {
          this.requestedReports = data.object
            // Filter out NOT_PAID reports
            .filter((report) => report.status !== ReportStatus.ORDERED_NOT_PAID)
            .sort((a, b) => {
              // Check if either report has the status 'READY'
              // const isAReady = a.status === ReportStatus.READY;
              // const isBReady = b.status === ReportStatus.READY;

              // Prioritize 'READY' status
              // if (isAReady && !isBReady) return -1;
              // if (!isAReady && isBReady) return 1;

              // If statuses are the same compare by orderedAt
              const dateA = new Date(a.orderedAt);
              const dateB = new Date(b.orderedAt);
              return dateB.getTime() - dateA.getTime();
            });
          // Mark for polling
          this.isSomeReportInProgress = this.requestedReports.some((report) =>
            this.reportsService.isReportInProgress(report)
          );
        }),
        // Continue polling if there's at least one report in the ORDERED or IN_PROGRESS status
        // The second parameter (true) ensures the final emission (that doesn't satisfy the condition) is still passed through
        takeWhile(
          (data: JsonResponse<RequestedReport[]>) =>
            data.object.some((report) =>
              this.reportsService.isReportInProgress(report)
            ),
          true
        )
      )
      .subscribe({
        complete: () => {
          // Once polling stops, mark that polling is done.
          this.isSomeReportInProgress = false;
        },
      });
  }

  public handleLinkClick(report: RequestedReport): void {
    if (report.url) {
      window.open(report.url, '_blank');
    }
  }

  protected readonly ReportStatus = ReportStatus;
  protected readonly activeReportTypesToTitle = activeReportTypesToTitle;
}
